import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { CommentContext, ContentReferenceContext } from '@gitbook/app-database';
import type { NavigationCommentsSideSheet } from '@gitbook/app-navigation';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient, useHiveRevision } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import {
    BreadcrumbButton,
    BreadcrumbItem,
    BreadcrumbSpan,
    Breadcrumbs,
    Description,
    EmptyState,
    EmptyStateIcon,
    Heading,
    Pagination,
    SegmentedControl,
    SidePanel,
    SidePanelBody,
    SidePanelBodyContent,
    SidePanelCloseButton,
    SidePanelExpandButton,
    SidePanelHeader,
    SidePanelHeaderActions,
    SidePanelHeaderMain,
    SidePanelHeaderSecondary,
    SidePanelHeaderTitle,
    Tooltip,
} from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { apiQuery } from '../../state';
import { APIErrorBoundary } from '../APIErrorBoundary';
import { ContentReferenceContextProvider } from '../ContentReferences';
import { formatUserPrettyName } from '../Users';
import { CommentCard } from './CommentCard';
import { CommentBoxSkeleton } from './CommentSkeleton';
import { CommentStatusFilters, FilterCommentsButton } from './FilterCommentsButton';
import { PostCommentBox } from './PostCommentBox';
import { useListPaginatedComments } from './useListPaginatedComments';
import { isCommentOnDeletedNodeOrPage } from './utils';

const COMMENT_SKELETON_COUNT = 3;

enum CommentsSidePanelTabs {
    All = 'all',
    Page = 'page',
}

const copy = {
    space: 'Leave comments on the content, or use the editor to comment on a specific block.',
    cr: 'Describe the edits in this change request and use @someone to collaborate.',
};

export type CommentsSidePanelState = {
    currentPage: string | null;
    sideSheet: NavigationCommentsSideSheet;
};

/**
 * SideSheet to display the comments on a space.
 */
export function CommentsSidePanel(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    analyticsContext: analytics.AnalyticsContextSpaceContent;

    /** Current state of the side panel to display */
    state: CommentsSidePanelState;

    /** Callback to navigate to a new state */
    onNavigate: (state: CommentsSidePanelState) => void;

    /** Callback to close the side panel */
    onClose: () => void;
}): React.ReactElement {
    const { space, changeRequest, state, onNavigate, onClose } = props;

    const { revision } = useHiveRevision();
    const currentPage =
        state.currentPage &&
        revision.pages[state.currentPage] &&
        hiveCore.isRevisionPageDocument(revision.pages[state.currentPage])
            ? (revision.pages[state.currentPage] as hiveCore.RevisionPageDocument)
            : null;

    const { sideSheet } = state;

    const [selectedTab, setSelectedTab] = useStateWithDeps<CommentsSidePanelTabs>(
        currentPage ? CommentsSidePanelTabs.Page : CommentsSidePanelTabs.All,
        [state.sideSheet.tab]
    );

    const setOpenedComment = (comment: api.Comment | undefined) => {
        onNavigate({
            currentPage: comment?.target.page?.id || null,
            sideSheet: {
                type: 'comments',
                node: comment?.target.node?.id || undefined,
                ...(comment?.id ? { comment: comment.id } : {}),
                ...(comment?.target.tableCell
                    ? {
                          tableCell: {
                              record: comment.target.tableCell.record,
                              definition: comment.target.tableCell.definition,
                          },
                      }
                    : {}),
            },
        });
    };

    const contentReferenceContext: ContentReferenceContext = {
        organization: space.organization,
        space: space.id,
        page: currentPage?.id,
        changeRequest: changeRequest?.id,
    };

    const [statusFilter, setStatusFilter] = React.useState<CommentStatusFilters>(
        CommentStatusFilters.Open
    );
    const [authorsFilter, setAuthorsFilter] = React.useState<Set<string>>(new Set());

    const filterButton = (
        <FilterCommentsButton
            spaceId={space.id}
            changeRequestId={changeRequest?.id}
            statusFilter={statusFilter}
            setStatusFilter={(status) => setStatusFilter(status)}
            authorsFilter={authorsFilter}
            setAuthorsFilter={(authors) => setAuthorsFilter(authors)}
        />
    );

    const onGoToAllComments = () => {
        setOpenedComment(undefined);
    };

    return (
        <ContentReferenceContextProvider value={contentReferenceContext}>
            <SidePanel expandable onClose={onClose}>
                {sideSheet.comment ? (
                    <React.Suspense
                        fallback={
                            <>
                                <SidePanelHeader>
                                    <SidePanelHeaderMain>
                                        <SidePanelHeaderTitle>
                                            <Heading>Comments</Heading>
                                        </SidePanelHeaderTitle>
                                    </SidePanelHeaderMain>
                                    <SidePanelHeaderSecondary>
                                        <Breadcrumbs label="Comments breadcrumbs">
                                            <BreadcrumbItem>
                                                <BreadcrumbButton
                                                    isActive={false}
                                                    onPress={onGoToAllComments}
                                                >
                                                    All
                                                </BreadcrumbButton>
                                            </BreadcrumbItem>
                                            <BreadcrumbItem>
                                                <BreadcrumbSpan isCurrent>Comment</BreadcrumbSpan>
                                            </BreadcrumbItem>
                                        </Breadcrumbs>
                                    </SidePanelHeaderSecondary>
                                </SidePanelHeader>
                                <SidePanelBody>
                                    <SidePanelBodyContent>
                                        <CommentBoxSkeleton />
                                    </SidePanelBodyContent>
                                </SidePanelBody>
                            </>
                        }
                    >
                        <OpenedCommentBody
                            space={space}
                            changeRequest={changeRequest}
                            openedCommentId={sideSheet.comment}
                            showContextLink={selectedTab === CommentsSidePanelTabs.All}
                            onGoToAllComments={onGoToAllComments}
                        />
                    </React.Suspense>
                ) : (
                    <>
                        <SidePanelHeader>
                            <SidePanelHeaderMain>
                                <SidePanelHeaderTitle>
                                    <Heading>Comments</Heading>
                                </SidePanelHeaderTitle>
                                <SidePanelHeaderActions>
                                    {currentPage ? null : filterButton}
                                    <SidePanelExpandButton />
                                    <SidePanelCloseButton />
                                </SidePanelHeaderActions>
                            </SidePanelHeaderMain>
                            {currentPage ? (
                                <SidePanelHeaderSecondary className="justify-between">
                                    <SegmentedControl<CommentsSidePanelTabs>
                                        options={[
                                            {
                                                value: CommentsSidePanelTabs.Page,
                                                label: 'This page',
                                            },
                                            {
                                                value: CommentsSidePanelTabs.All,
                                                label: 'All pages',
                                            },
                                        ]}
                                        size="small"
                                        active={selectedTab}
                                        onChange={(key) => setSelectedTab(key)}
                                    />
                                    {filterButton}
                                </SidePanelHeaderSecondary>
                            ) : null}
                        </SidePanelHeader>
                        <SidePanelBody>
                            <ListCommentsBody
                                space={space}
                                changeRequest={changeRequest}
                                currentPage={currentPage}
                                sideSheet={sideSheet}
                                selectedTab={selectedTab}
                                statusFilter={statusFilter}
                                authorsFilter={authorsFilter}
                                onOpenComment={setOpenedComment}
                            />
                        </SidePanelBody>
                    </>
                )}
            </SidePanel>
        </ContentReferenceContextProvider>
    );
}

function ListCommentsBody(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    currentPage: hiveCore.RevisionPageDocument | null;
    sideSheet: NavigationCommentsSideSheet;
    selectedTab: CommentsSidePanelTabs;
    statusFilter: CommentStatusFilters;
    authorsFilter: Set<string>;
    onOpenComment: (comment: api.Comment) => void;
}) {
    const {
        space,
        changeRequest,
        currentPage,
        selectedTab,
        sideSheet,
        statusFilter,
        authorsFilter,
        onOpenComment,
    } = props;

    // Fetches all comments, filtered by page, status and author.
    const [comments, next, previous] = useListPaginatedComments({
        spaceId: space.id,
        changeRequestId: changeRequest?.id || undefined,
        pageId:
            selectedTab === CommentsSidePanelTabs.Page && currentPage?.id
                ? currentPage.id
                : undefined,
        status: statusFilter,
        authors: authorsFilter,
    });

    /**
     * The context in which new comments will be created in. It depends on how the
     * sidesheet was opened.
     */
    const postCommentContext: CommentContext = React.useMemo(
        () => ({
            space: space.id,
            changeRequest: changeRequest?.id || null,
            page: currentPage?.id,

            // If a node is selected and we were in the context of a new post, add the node and the sheet to the context
            ...(sideSheet.node && sideSheet.context === 'post'
                ? {
                      node: sideSheet.node,
                      ...(sideSheet.tableCell
                          ? {
                                tableCell: {
                                    record: sideSheet.tableCell.record,
                                    definition: sideSheet.tableCell.definition,
                                },
                            }
                          : {}),
                  }
                : {}),
        }),
        [space.id, changeRequest?.id, sideSheet, currentPage?.id]
    );

    return (
        <>
            {/* Ensure the container is `relative` so that any focus in this container remains trapped within it and doesn't cause the entire document to shift and offset */}
            <div className="scrollbar relative flex flex-1 flex-col overflow-y-auto">
                <React.Suspense
                    fallback={
                        <div className="flex h-full w-full flex-col justify-start p-2">
                            {Array.from({ length: COMMENT_SKELETON_COUNT }, (_, i) => (
                                <CommentBoxSkeleton key={i} />
                            ))}
                        </div>
                    }
                >
                    <div className="flex w-full flex-1 flex-col gap-2.5 bg-base">
                        {comments.length === 0 ? (
                            <CommentsEmptyState
                                selectedTab={selectedTab}
                                isInChangeRequest={!!changeRequest}
                            />
                        ) : (
                            <CommentsList
                                space={space}
                                changeRequest={changeRequest}
                                comments={comments}
                                onOpenComment={onOpenComment}
                                next={next}
                                previous={previous}
                                selectedTab={selectedTab}
                            />
                        )}
                    </div>
                </React.Suspense>
            </div>
            {currentPage ? (
                <PostCommentBox currentPage={currentPage} context={postCommentContext} />
            ) : null}
        </>
    );
}

function OpenedCommentBody(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    openedCommentId: string;
    showContextLink: boolean;
    onGoToAllComments: () => void;
}) {
    const { space, changeRequest, openedCommentId, showContextLink, onGoToAllComments } = props;

    const { data: openedComment } = useSuspenseQuery(
        changeRequest
            ? apiQuery(
                  'getCommentInChangeRequest',
                  {
                      spaceId: space.id,
                      changeRequestId: changeRequest.id,
                      commentId: openedCommentId,
                  },
                  { nullOnError: true }
              )
            : apiQuery(
                  'getCommentInSpace',
                  {
                      spaceId: space.id,
                      commentId: openedCommentId,
                  },
                  { nullOnError: true }
              )
    );

    return (
        <>
            <SidePanelHeader>
                <SidePanelHeaderMain>
                    <SidePanelHeaderTitle>
                        <Heading>Comments</Heading>
                    </SidePanelHeaderTitle>
                    <SidePanelHeaderActions>
                        <SidePanelExpandButton />
                        <SidePanelCloseButton />
                    </SidePanelHeaderActions>
                </SidePanelHeaderMain>
                <SidePanelHeaderSecondary>
                    <Breadcrumbs label="Comments breadcrumbs">
                        <BreadcrumbItem>
                            <BreadcrumbButton
                                isActive={false}
                                onPress={() => onGoToAllComments()}
                                className="w-fit"
                            >
                                All
                            </BreadcrumbButton>
                        </BreadcrumbItem>
                        {openedComment ? (
                            <BreadcrumbItem>
                                <BreadcrumbSpan isCurrent>
                                    Comment from{' '}
                                    {openedComment.postedBy?.displayName
                                        ? formatUserPrettyName(openedComment.postedBy.displayName)
                                        : 'someone'}
                                </BreadcrumbSpan>
                            </BreadcrumbItem>
                        ) : null}
                    </Breadcrumbs>
                </SidePanelHeaderSecondary>
            </SidePanelHeader>
            {/* Ensure the container is `relative` so that any focus in this container remains trapped within it and doesn't cause the entire document to shift and offset */}
            <SidePanelBody className="scrollbar relative flex flex-1 flex-col overflow-y-auto p-2">
                <React.Suspense
                    fallback={
                        <div className="flex h-full w-full flex-col justify-start">
                            <CommentBoxSkeleton />
                        </div>
                    }
                >
                    <div className="flex w-full flex-1 flex-col gap-2.5 bg-base">
                        {openedComment ? (
                            <CommentCard
                                space={space}
                                changeRequest={changeRequest}
                                comment={openedComment}
                                showContextLink={showContextLink}
                                isSelected
                                className="rounded-xl"
                            />
                        ) : (
                            <EmptyState>
                                <EmptyStateIcon>
                                    <Icon.Comment />
                                </EmptyStateIcon>
                                <Heading>Comment not found</Heading>
                                <Description>
                                    The comment you are looking for could not be found.
                                </Description>
                            </EmptyState>
                        )}
                    </div>
                </React.Suspense>
            </SidePanelBody>
        </>
    );
}

function CommentsEmptyState(props: {
    selectedTab: CommentsSidePanelTabs;
    isInChangeRequest: boolean;
}): React.ReactElement {
    const { selectedTab, isInChangeRequest } = props;

    const title = (() => {
        if (selectedTab === CommentsSidePanelTabs.Page) {
            return 'No comments on this page';
        }

        if (selectedTab === CommentsSidePanelTabs.All) {
            if (isInChangeRequest) {
                return 'No comments in this change request';
            }

            return 'No comments in this space';
        }

        assertNever(selectedTab);
    })();

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Comment />
            </EmptyStateIcon>
            <Heading>{title}</Heading>
            <Description>{isInChangeRequest ? copy.cr : copy.space}</Description>
        </EmptyState>
    );
}

function CommentsList(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    comments: Array<api.Comment>;
    next: (() => void | Promise<void>) | undefined;
    previous: (() => void | Promise<void>) | undefined;
    selectedTab: CommentsSidePanelTabs;
    onOpenComment: (comment: api.Comment) => void;
}): React.ReactElement {
    const {
        space,
        changeRequest,
        comments: commentsList,
        next,
        previous,
        onOpenComment,
        selectedTab,
    } = props;

    const hiveClient = useHiveClient();
    const { revision } = useHiveRevision();

    // Sort the comments so the ones on deleted nodes/pages are always at the bottom of the list
    const comments = commentsList.slice().sort((aComment, bComment) => {
        const aIsOnDeletedNodeOrPage = isCommentOnDeletedNodeOrPage(aComment, revision, hiveClient);
        const bIsOnDeletedNodeOrPage = isCommentOnDeletedNodeOrPage(bComment, revision, hiveClient);

        // If both comments are on deleted nodes or pages, sort by postedAt
        if (aIsOnDeletedNodeOrPage && bIsOnDeletedNodeOrPage) {
            return new Date(bComment.postedAt).getTime() - new Date(aComment.postedAt).getTime();
        }

        // If only one comment is on a deleted node or page, sort it to the bottom
        if (aIsOnDeletedNodeOrPage) {
            return 1;
        }

        if (bIsOnDeletedNodeOrPage) {
            return -1;
        }

        return 0;
    });

    return (
        <div className="flex flex-1 flex-col p-2">
            {comments.map((comment) => {
                return (
                    <APIErrorBoundary
                        fallback={{
                            404: (
                                <Tooltip label="Comment not found">
                                    <CommentBoxSkeleton />
                                </Tooltip>
                            ),
                        }}
                        key={comment.id}
                    >
                        <CommentCard
                            space={space}
                            changeRequest={changeRequest}
                            comment={comment}
                            showContextLink={selectedTab === CommentsSidePanelTabs.All}
                            isSelected={false}
                            onPress={() => onOpenComment(comment)}
                            className="rounded-xl hover:bg-side"
                        />
                    </APIErrorBoundary>
                );
            })}

            {next || previous ? (
                <Pagination className="px-5 pb-5" previous={previous} next={next} />
            ) : null}
        </div>
    );
}
